import { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { PlayArrow as PlayIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import api from '../services/api';
import toast from 'react-hot-toast';
import ConfigCard from '../components/AutoSubscriber/ConfigCard';
import AccountFilterBar from '../components/common/AccountFilterBar';
import { useAutoSubscriberWebSocket, type AutoSubscriberProgress } from '../hooks/useAutoSubscriberWebSocket';

export default function AutoSubscriberPage() {
  const [configs, setConfigs] = useState<any[]>([]);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [filterAccountId, setFilterAccountId] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [progressMap, setProgressMap] = useState<Record<number, AutoSubscriberProgress>>({});
  const [formData, setFormData] = useState({
    accountId: '',
    name: '',
    targetGroups: '',
    maxSubscriptionsPerDay: 30,
    filterByActivity: false,
    filterByLocation: false,
    location: '',
    filterByAge: false,
    minAge: 18,
    maxAge: 65,
  });

  const loadData = async () => {
    setLoading(true);
    try {
      const [cfgRes, accRes] = await Promise.all([
        api.get('/auto-subscriber'),
        api.get('/vk-accounts'),
      ]);
      setConfigs(cfgRes.data.data.configs);
      setAccounts(accRes.data.data.accounts);
    } catch (err) {
      toast.error('Не удалось загрузить данные');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // WebSocket handlers
  const handleAction = useCallback((data: any) => {
    console.log('New subscription action:', data);

    // Update config stats
    setConfigs((prev) =>
      prev.map((cfg) =>
        cfg.id === data.configId
          ? { ...cfg, todaySubscriptions: cfg.todaySubscriptions + 1, totalSubscriptions: cfg.totalSubscriptions + 1 }
          : cfg
      )
    );

    // Add action to the list
    const addActionFn = (window as any)[`addSubscriberActionToConfig${data.configId}`];
    if (addActionFn) {
      addActionFn({ ...data.action, vkData: data.vkData });
    }

    toast.success(`Новая подписка: ${data.vkData?.userName || 'неизвестный пользователь'}`);
  }, []);

  const handleStatus = useCallback((data: any) => {
    console.log('Status update:', data);
    setConfigs((prev) =>
      prev.map((cfg) =>
        cfg.id === data.configId
          ? { ...cfg, todaySubscriptions: data.todaySubscriptions, isActive: data.isActive }
          : cfg
      )
    );
  }, []);

  const handleError = useCallback((data: any) => {
    console.log('Error:', data);
    toast.error(`Правило #${data.configId}: ${data.error}`);
  }, []);

  const handleProgress = useCallback((data: AutoSubscriberProgress) => {
    console.log('Progress update:', data);
    setProgressMap((prev) => ({
      ...prev,
      [data.configId]: data,
    }));

    // Clear completed/error progress after 30 seconds
    if (data.phase === 'completed' || data.phase === 'error') {
      setTimeout(() => {
        setProgressMap((prev) => {
          const newMap = { ...prev };
          if (newMap[data.configId]?.phase === data.phase) {
            delete newMap[data.configId];
          }
          return newMap;
        });
      }, 30000);
    }
  }, []);

  useAutoSubscriberWebSocket({
    onAction: handleAction,
    onStatus: handleStatus,
    onError: handleError,
    onProgress: handleProgress,
  });

  const handleCreate = async () => {
    try {
      const targetGroups = formData.targetGroups
        .split('\n')
        .map((g) => g.trim())
        .filter((g) => g.length > 0);

      if (targetGroups.length === 0) {
        toast.error('Укажите хотя бы одну ссылку на группу');
        return;
      }

      const filters: any = {};
      if (formData.filterByActivity) {
        filters.activityDays = 30; // Active in last 30 days
      }
      if (formData.filterByLocation && formData.location) {
        filters.location = formData.location;
      }
      if (formData.filterByAge) {
        filters.minAge = formData.minAge;
        filters.maxAge = formData.maxAge;
      }

      await api.post('/auto-subscriber', {
        accountId: parseInt(formData.accountId),
        name: formData.name,
        targetGroupsUrls: targetGroups,
        maxSubscriptionsPerDay: formData.maxSubscriptionsPerDay,
        filters: Object.keys(filters).length > 0 ? filters : undefined,
      });
      toast.success('Правило создано!');
      setOpen(false);
      setFormData({
        accountId: '',
        name: '',
        targetGroups: '',
        maxSubscriptionsPerDay: 30,
        filterByActivity: false,
        filterByLocation: false,
        location: '',
        filterByAge: false,
        minAge: 18,
        maxAge: 65,
      });
      loadData();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Не удалось создать правило');
    }
  };

  const handleRunAll = async () => {
    setRunning(true);
    try {
      const res = await api.post('/auto-subscriber/run-all');
      const { queued, total } = res.data.data;
      toast.success(`Запущено ${queued} из ${total} активных правил`);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Не удалось запустить');
    } finally {
      setRunning(false);
    }
  };

  const handleDelete = (id: number) => {
    setConfigs((prev) => prev.filter((cfg) => cfg.id !== id));
  };

  const visibleConfigs = filterAccountId
    ? configs.filter((cfg) => String(cfg.accountId) === filterAccountId)
    : configs;
  const activeCount = configs.filter((cfg) => cfg.isActive).length;

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Автоподписчик</Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={running ? <CircularProgress size={20} /> : <PlayIcon />}
            onClick={handleRunAll}
            disabled={running || activeCount === 0}
          >
            Запустить все активные ({activeCount})
          </Button>
          <Button variant="contained" onClick={() => setOpen(true)}>
            Создать правило
          </Button>
        </Box>
      </Box>

      <Alert severity="warning" sx={{ mb: 2 }}>
        Заявки в друзья — главная причина заморозки VK. Для новых аккаунтов (моложе 14 дней)
        автоподписчик автоматически на паузе, а лимиты первых недель занижены. Начинайте
        осторожно: 10–15 заявок в день.
      </Alert>

      <AccountFilterBar accounts={accounts} value={filterAccountId} onChange={setFilterAccountId} />

      {configs.length === 0 ? (
        <Alert severity="info">
          Пока нет ни одного правила. Создайте первое правило автоподписчика, чтобы начать.
        </Alert>
      ) : visibleConfigs.length === 0 ? (
        <Alert severity="info">Нет правил для выбранного аккаунта.</Alert>
      ) : (
        <Box>
          {visibleConfigs.map((cfg) => (
            <ConfigCard
              key={cfg.id}
              config={cfg}
              onUpdate={loadData}
              onDelete={handleDelete}
              progress={progressMap[cfg.id] || null}
            />
          ))}
        </Box>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Новое правило автоподписчика</DialogTitle>
        <DialogContent>
          <TextField
            select
            fullWidth
            label="VK Аккаунт"
            value={formData.accountId}
            onChange={(e) => setFormData({ ...formData, accountId: e.target.value })}
            margin="normal"
            SelectProps={{ native: true }}
          >
            <option value="">Выберите аккаунт</option>
            {accounts.map((acc) => (
              <option key={acc.id} value={acc.id}>
                {acc.firstName} {acc.lastName}
              </option>
            ))}
          </TextField>
          <TextField
            fullWidth
            label="Название правила"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Целевые группы (по одной ссылке на строку)"
            value={formData.targetGroups}
            onChange={(e) => setFormData({ ...formData, targetGroups: e.target.value })}
            margin="normal"
            helperText="Из этих групп берутся участники для подписки. Пример: vk.com/group1"
          />
          <TextField
            fullWidth
            type="number"
            label="Макс. подписок в день"
            value={formData.maxSubscriptionsPerDay}
            onChange={(e) =>
              setFormData({ ...formData, maxSubscriptionsPerDay: parseInt(e.target.value) })
            }
            margin="normal"
            inputProps={{ min: 1, max: 100 }}
          />

          <Accordion sx={{ mt: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>Фильтры (необязательно)</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.filterByActivity}
                    onChange={(e) =>
                      setFormData({ ...formData, filterByActivity: e.target.checked })
                    }
                  />
                }
                label="Только активные пользователи (заходили за последние 30 дней)"
              />

              <Box sx={{ mt: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.filterByLocation}
                      onChange={(e) =>
                        setFormData({ ...formData, filterByLocation: e.target.checked })
                      }
                    />
                  }
                  label="Фильтр по городу"
                />
                {formData.filterByLocation && (
                  <TextField
                    fullWidth
                    label="Город"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    margin="dense"
                    size="small"
                    helperText="например: Москва, Санкт-Петербург"
                  />
                )}
              </Box>

              <Box sx={{ mt: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.filterByAge}
                      onChange={(e) =>
                        setFormData({ ...formData, filterByAge: e.target.checked })
                      }
                    />
                  }
                  label="Фильтр по возрасту"
                />
                {formData.filterByAge && (
                  <Box display="flex" gap={2} mt={1}>
                    <TextField
                      type="number"
                      label="Возраст от"
                      value={formData.minAge}
                      onChange={(e) =>
                        setFormData({ ...formData, minAge: parseInt(e.target.value) })
                      }
                      size="small"
                      inputProps={{ min: 14, max: 100 }}
                    />
                    <TextField
                      type="number"
                      label="Возраст до"
                      value={formData.maxAge}
                      onChange={(e) =>
                        setFormData({ ...formData, maxAge: parseInt(e.target.value) })
                      }
                      size="small"
                      inputProps={{ min: 14, max: 100 }}
                    />
                  </Box>
                )}
              </Box>
            </AccordionDetails>
          </Accordion>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Отмена</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={!formData.accountId || !formData.name || !formData.targetGroups.trim()}
          >
            Создать
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
